import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, Phone, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import api, { fmtDate, money2 } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal print:hidden";

const mapsUrl = (b) =>
  `https://maps.google.com/?q=${encodeURIComponent(`${b.customer?.address || ""} ${b.delivery_zip || ""}`.trim())}`;

const WINDOW_LABELS = { morning: "9am–12pm", evening: "4pm–7pm", call: "call to discuss" };

const sortStops = (list) =>
  [...list].sort((a, b) =>
    (a.delivery_zip || "").localeCompare(b.delivery_zip || "") ||
    (a.customer?.address || "").localeCompare(b.customer?.address || ""));

const StopCard = ({ b, n, kind }) => {
  const done = kind === "deliver" ? b.fulfillment === "delivered" || b.fulfillment === "picked_up" : b.fulfillment === "picked_up";
  const extras = [
    b.dollies > 1 ? `${b.dollies} dollies` : null,
    b.extra_dollies > 0 ? `${b.extra_dollies} extra dollies` : null,
    b.dolly_bundle ? "label bundle" : null,
    b.packing_kit ? "packing kit" : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className={`flex gap-4 break-inside-avoid rounded-2xl border border-slate-200 bg-white p-5 print:rounded-none print:border-0 print:border-b print:border-slate-300 print:p-4 ${done ? "opacity-50" : ""}`}
         data-testid={`route-stop-${b.id}`}>
      <div className="flex flex-col items-center gap-2">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink font-display text-sm font-extrabold text-white print:border print:border-ink print:bg-white print:text-ink">
          {n}
        </span>
        <span className="mt-1 h-5 w-5 rounded-md border-2 border-slate-300 print:border-ink" aria-label="checkbox" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] ${kind === "deliver" ? "bg-royal-light text-royal" : "bg-amber-50 text-amber-700"}`}>
            {kind === "deliver" ? "Deliver" : "Pick up"}
          </span>
          <span className="font-mono text-xs font-semibold text-slate-500">{b.reference}</span>
          {done && <span className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600">done</span>}
          {b.self_pickup && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600">self pickup — warehouse</span>}
        </div>
        <p className="mt-1.5 font-display text-base font-bold text-ink">{b.customer?.name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {b.customer?.phone && (
            <a href={`tel:${b.customer.phone}`} data-testid={`route-call-${b.id}`}
               className="flex items-center gap-1.5 font-semibold text-royal hover:underline print:text-ink print:no-underline">
              <Phone className="h-3.5 w-3.5 print:hidden" /> {b.customer.phone}
            </a>
          )}
          <a href={mapsUrl(b)} target="_blank" rel="noreferrer" data-testid={`route-map-${b.id}`}
             className="flex items-center gap-1.5 text-slate-600 hover:text-royal hover:underline print:text-ink print:no-underline">
            <MapPin className="h-3.5 w-3.5 print:hidden" /> {b.customer?.address}{b.delivery_zip ? `, ${b.delivery_zip}` : ""}
          </a>
        </div>
        <p className="mt-1.5 text-xs text-slate-600">
          <span className="font-semibold text-ink">{b.listing_title}</span>
          {b.tote_qty ? ` · ${b.tote_qty} totes` : ""}
          {extras ? ` · ${extras}` : ""}
          {` · ${kind === "deliver" ? "drop" : "collect"} ${WINDOW_LABELS[b[kind === "deliver" ? "delivery_window" : "pickup_window"]] || "9am–12pm"}`}
        </p>
        {kind === "deliver" && b.balance_due > 0 && (
          <p className="mt-1 text-xs font-bold text-amber-700">Collect {money2(b.balance_due)} balance on delivery</p>
        )}
        {b.customer?.notes && (
          <p className="mt-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 print:bg-transparent print:p-0">
            Note: {b.customer.notes}
          </p>
        )}
      </div>
    </div>
  );
};

export default function RouteSheetPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const date = searchParams.get("date") || today;
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
    }
  }, [user]);

  const { deliveries, pickups } = useMemo(() => {
    const live = (bookings || []).filter((b) => b.status === "confirmed");
    return {
      deliveries: sortStops(live.filter((b) => b.start_date === date)),
      pickups: sortStops(live.filter((b) => b.end_date === date)),
    };
  }, [bookings, date]);

  if (user === null) return <main className="min-h-[60vh]" />;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const dayLabel = fmtDate(date);
  let n = 0;

  return (
    <main className="bg-paper pb-20 print:bg-white" data-testid="route-sheet-page">
      <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/admin" className="flex items-center gap-1.5 text-xs font-semibold text-royal hover:underline print:hidden">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Command deck
            </Link>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-ink" data-testid="route-sheet-title">
              Route sheet — {dayLabel}
            </h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {deliveries.length} deliver{deliveries.length === 1 ? "y" : "ies"} · {pickups.length} pickup{pickups.length === 1 ? "" : "s"} · ordered by zip
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setSearchParams({ date: e.target.value })}
              data-testid="route-date-input"
              className={inputCls}
            />
            <button
              onClick={() => window.print()}
              data-testid="route-print-button"
              className="flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>

        {bookings === null ? (
          <p className="mt-12 text-center text-sm text-slate-400">Loading the day's runs…</p>
        ) : (
          <>
            <h2 className="mt-10 border-b-2 border-ink pb-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink" data-testid="route-deliveries-heading">
              Morning — deliveries
            </h2>
            <div className="mt-4 space-y-3 print:space-y-0">
              {deliveries.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400 print:border-0">
                  No deliveries on {dayLabel}.
                </p>
              )}
              {deliveries.map((b) => { n += 1; return <StopCard key={b.id} b={b} n={n} kind="deliver" />; })}
            </div>

            <h2 className="mt-10 border-b-2 border-ink pb-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink" data-testid="route-pickups-heading">
              Afternoon — pickups
            </h2>
            <div className="mt-4 space-y-3 print:space-y-0">
              {pickups.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400 print:border-0">
                  No pickups on {dayLabel}.
                </p>
              )}
              {pickups.map((b) => { n += 1; return <StopCard key={b.id} b={b} n={n} kind="pickup" />; })}
            </div>

            <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Oregon Tote Rentals · (541) 408-7019 · reservations@oregon-tote-rentals.com
            </p>
          </>
        )}
      </div>
    </main>
  );
}
